import { ReactElement } from 'react';

interface RangeSliderProps {
  min: number;
  max: number;
  step?: number;
  low: number;
  high: number;
  onChange: (low: number, high: number) => void;
  ariaLabelLow?: string;
  ariaLabelHigh?: string;
}

const ACCENT = '#03365D';
const TRACK = '#E5E7EB';

/**
 * The thumb styling has to be real CSS — `::-webkit-slider-thumb` and friends
 * cannot be expressed as Tailwind utilities and this repo has no slider-thumb
 * variant plugin. Injected once at module scope rather than as a `<style>` in the
 * render tree, so mounting a second slider doesn't duplicate the rules and
 * toggling one doesn't churn the document's stylesheets.
 */
const RANGE_CSS = `
.range-slider { position: relative; height: 20px; flex: 1 1 auto; }
.range-slider-track { position: absolute; top: 8px; left: 0; right: 0; height: 4px;
  border-radius: 9999px; }
.range-slider-input { position: absolute; top: 0; left: 0; width: 100%; height: 20px; margin: 0;
  background: transparent; pointer-events: none; -webkit-appearance: none; appearance: none; }
.range-slider-input:focus { outline: none; }
.range-slider-input::-webkit-slider-runnable-track { height: 20px; background: transparent; border: none; }
.range-slider-input::-moz-range-track { height: 20px; background: transparent; border: none; }
.range-slider-input::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; pointer-events: auto;
  width: 14px; height: 14px; margin-top: 3px; border-radius: 50%; background: ${ACCENT};
  border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.35); cursor: pointer; }
.range-slider-input::-moz-range-thumb { pointer-events: auto; width: 14px; height: 14px;
  border-radius: 50%; background: ${ACCENT}; border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.35); cursor: pointer; }
.range-slider-input:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px rgba(3,54,93,0.35); }
.range-slider-input:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 3px rgba(3,54,93,0.35); }
`;

const STYLE_ID = 'range-slider-styles';

if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = RANGE_CSS;
  document.head.appendChild(el);
}

/** Fraction of the way along the track, guarded against a zero-width domain. */
function pct(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * 100;
}

/**
 * Two-thumb range slider built from a pair of overlaid native `input[type=range]`
 * elements — the low and high thumbs stay individually keyboard-accessible while
 * reading as one control. The inputs themselves are `pointer-events: none` with
 * only their thumbs clickable, so whichever thumb is nearer the pointer wins
 * instead of the top input swallowing every drag. The tradeoff is that clicking
 * the bare track does nothing; the thumbs must be dragged.
 */
export default function RangeSlider({
  min,
  max,
  step = 1,
  low,
  high,
  onChange,
  ariaLabelLow = 'Minimum',
  ariaLabelHigh = 'Maximum',
}: RangeSliderProps): ReactElement {
  // Thumbs may meet but not cross: each is clamped against the other.
  const handleLow = (value: number) => onChange(Math.min(value, high), high);
  const handleHigh = (value: number) => onChange(low, Math.max(value, low));

  const lowPct = pct(low, min, max);
  const highPct = pct(high, min, max);

  // With both thumbs pinned at the top of the range the upper input sits on top
  // and cannot move any further, so the pair would be stuck. Lift the lower input
  // above it in that case; mouse users can then drag the window back open.
  const lowOnTop = low >= max;

  return (
    <div className="range-slider">
      <div
        className="range-slider-track"
        style={{
          background:
            `linear-gradient(to right, ${TRACK} 0%, ${TRACK} ${lowPct}%, ${ACCENT} ${lowPct}%, ` +
            `${ACCENT} ${highPct}%, ${TRACK} ${highPct}%, ${TRACK} 100%)`,
        }}
      />
      <input
        type="range"
        className="range-slider-input"
        aria-label={ariaLabelLow}
        min={min}
        max={max}
        step={step}
        value={low}
        style={{ zIndex: lowOnTop ? 2 : 1 }}
        onChange={(e) => handleLow(parseFloat(e.target.value))}
      />
      <input
        type="range"
        className="range-slider-input"
        aria-label={ariaLabelHigh}
        min={min}
        max={max}
        step={step}
        value={high}
        style={{ zIndex: lowOnTop ? 1 : 2 }}
        onChange={(e) => handleHigh(parseFloat(e.target.value))}
      />
    </div>
  );
}
